/**
 * Sanitize operator-message HTML for London Smoke & Cure.
 * Allows basic formatting: bold, italic, underline, strikethrough, lists, coloured spans.
 */

const ALLOWED_COLORS = new Set([
    '#1f2933',
    '#222222',
    '#B99040',
    '#8B6F2E',
    '#0d9488',
    '#b91c1c',
    '#ffffff',
]);

function normalizeColor(value) {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim().toLowerCase();
    const hexMatch = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!hexMatch) return null;
    let hex = hexMatch[0].toUpperCase();
    if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex;
}

function isAllowedColor(hex) {
    if (!hex) return false;
    if (ALLOWED_COLORS.has(hex)) return true;
    return /^#[0-9A-F]{6}$/.test(hex);
}

/**
 * @param {string} dirtyHtml
 * @returns {{ bodyHtml: string, bodyText: string }}
 */
export function sanitizeOperatorMessageHtml(dirtyHtml) {
    const raw = typeof dirtyHtml === 'string' ? dirtyHtml : '';
    if (!raw.trim()) {
        return { bodyHtml: '', bodyText: '' };
    }

    if (typeof document === 'undefined') {
        const stripped = raw.replace(/<[^>]+>/g, '').trim();
        return { bodyHtml: escapeText(stripped), bodyText: stripped };
    }

    const template = document.createElement('template');
    template.innerHTML = raw;

    const out = document.createElement('div');

    const walk = (node, parent) => {
        if (node.nodeType === Node.TEXT_NODE) {
            parent.appendChild(document.createTextNode(node.textContent || ''));
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();

        if (tag === 'br') {
            parent.appendChild(document.createElement('br'));
            return;
        }

        if (tag === 'b' || tag === 'strong') {
            const el = document.createElement('strong');
            Array.from(node.childNodes).forEach((child) => walk(child, el));
            parent.appendChild(el);
            return;
        }

        if (tag === 'i' || tag === 'em') {
            const el = document.createElement('em');
            Array.from(node.childNodes).forEach((child) => walk(child, el));
            parent.appendChild(el);
            return;
        }

        if (tag === 'u') {
            const el = document.createElement('u');
            Array.from(node.childNodes).forEach((child) => walk(child, el));
            parent.appendChild(el);
            return;
        }

        if (tag === 's' || tag === 'strike' || tag === 'del') {
            const el = document.createElement('s');
            Array.from(node.childNodes).forEach((child) => walk(child, el));
            parent.appendChild(el);
            return;
        }

        if (tag === 'ul' || tag === 'ol') {
            const el = document.createElement(tag);
            Array.from(node.childNodes).forEach((child) => walk(child, el));
            parent.appendChild(el);
            return;
        }

        if (tag === 'li') {
            const el = document.createElement('li');
            Array.from(node.childNodes).forEach((child) => walk(child, el));
            parent.appendChild(el);
            return;
        }

        if (tag === 'div' || tag === 'p') {
            if (parent.childNodes.length > 0) {
                parent.appendChild(document.createElement('br'));
            }
            Array.from(node.childNodes).forEach((child) => walk(child, parent));
            return;
        }

        if (tag === 'span') {
            const color = normalizeColor(node.style?.color || '');
            if (color && isAllowedColor(color)) {
                const el = document.createElement('span');
                el.style.color = color;
                Array.from(node.childNodes).forEach((child) => walk(child, el));
                parent.appendChild(el);
                return;
            }
            Array.from(node.childNodes).forEach((child) => walk(child, parent));
            return;
        }

        // Font tags from execCommand
        if (tag === 'font') {
            const color = normalizeColor(node.getAttribute('color') || node.style?.color || '');
            if (color && isAllowedColor(color)) {
                const el = document.createElement('span');
                el.style.color = color;
                Array.from(node.childNodes).forEach((child) => walk(child, el));
                parent.appendChild(el);
                return;
            }
            Array.from(node.childNodes).forEach((child) => walk(child, parent));
            return;
        }

        Array.from(node.childNodes).forEach((child) => walk(child, parent));
    };

    Array.from(template.content.childNodes).forEach((child) => walk(child, out));

    const bodyHtml = out.innerHTML.trim();
    const bodyText = (out.textContent || '').replace(/\s+/g, ' ').trim();
    return { bodyHtml, bodyText };
}

function escapeText(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export const OPERATOR_MESSAGE_COLOR_PRESETS = [
    { id: 'charcoal', label: 'Charcoal', value: '#1f2933' },
    { id: 'gold', label: 'Gold', value: '#B99040' },
    { id: 'teal', label: 'Teal', value: '#0d9488' },
    { id: 'red', label: 'Red', value: '#b91c1c' },
];
